import { useCallback, useEffect, useRef, useState } from 'react'
import ErrorMessage from 'utils/ErrorMessage'
import { fetchAuditActions, fetchAuditEntityTypes, fetchAuditLogs } from '../api'
import { PAGE_SIZE } from '../constants'

const EMPTY_FILTERS = {
  from: undefined,
  to: undefined,
  action: undefined,
  actorId: undefined,
  entityType: undefined,
  portal: undefined,
  tenantId: undefined,
}

/**
 * Owns audit-log fetching, cursor paging and filter state.
 *
 * Paging is keyset, not offset: the trail is append-only and grows without
 * bound, so the API hands back a cursor rather than a page count. That is why
 * this exposes `loadMore` instead of an antd pagination config.
 *
 * @returns {object} Rows, filter state and the actions that change them.
 */
const useAuditLogs = () => {
  const [rows, setRows] = useState([])
  const [actions, setActions] = useState([])
  const [entityTypes, setEntityTypes] = useState([])
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [cursor, setCursor] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasError, setHasError] = useState(false)

  // Guards against an older, slower response overwriting a newer one when the
  // user changes filters quickly.
  const requestRef = useRef(0)

  const load = useCallback(
    async (activeFilters, nextCursor) => {
      const requestId = requestRef.current + 1
      requestRef.current = requestId

      const isAppending = Boolean(nextCursor)
      if (isAppending) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
      }
      setHasError(false)

      try {
        const params = { ...activeFilters, pageSize: PAGE_SIZE }
        if (nextCursor) {
          params.cursorOccurredAt = nextCursor.occurredAt
          params.cursorId = nextCursor.id
        }

        const data = await fetchAuditLogs(params)
        if (requestRef.current !== requestId) return

        setRows((current) =>
          isAppending ? [...current, ...data.items] : data.items
        )
        setCursor(data.nextCursor)
      } catch (error) {
        if (requestRef.current !== requestId) return
        setHasError(true)
        ErrorMessage(error?.response?.data?.message || 'Unable to load the audit log')
      } finally {
        if (requestRef.current === requestId) {
          setIsLoading(false)
          setIsLoadingMore(false)
        }
      }
    },
    []
  )

  useEffect(() => {
    load(filters, null)
  }, [filters, load])

  useEffect(() => {
    fetchAuditActions()
      .then(setActions)
      .catch(() => setActions([]))
    fetchAuditEntityTypes()
      .then(setEntityTypes)
      .catch(() => setEntityTypes([]))
  }, [])

  const applyFilters = useCallback((next) => {
    setRows([])
    setCursor(null)
    setFilters((current) => ({ ...current, ...next }))
  }, [])

  const resetFilters = useCallback(() => {
    setRows([])
    setCursor(null)
    setFilters(EMPTY_FILTERS)
  }, [])

  const loadMore = useCallback(() => {
    if (cursor) {
      load(filters, cursor)
    }
  }, [cursor, filters, load])

  const isFiltered = Object.values(filters).some(Boolean)

  return {
    rows,
    actions,
    entityTypes,
    filters,
    isFiltered,
    isLoading,
    isLoadingMore,
    hasError,
    hasMore: Boolean(cursor),
    applyFilters,
    resetFilters,
    loadMore,
    reload: () => load(filters, null),
  }
}

export default useAuditLogs
